import {$s, store} from '@bitstillery/common/app'

/**
 * Light/dark theme switching. The preference lives in the store as `$s.theme` (cookie-tier in the portal so
 * SSR can render it flash-free; `saved`/localStorage in discover). This module reads/writes that value and
 * mirrors it onto `<html data-theme>`; the visual flip itself is pure CSS (see `theme/tokens.css`).
 * No stored choice ('auto') = follow the OS via `prefers-color-scheme`.
 */
export type Theme = 'light' | 'dark'
/** User preference. `auto` follows the OS; light/dark force it. */
export type ThemeMode = 'light' | 'dark' | 'auto'

function has_dom(): boolean {
    return typeof document !== 'undefined' && !globalThis.__SSR_MODE__
}

/**
 * Theme forced by `?theme=`, for a page EMBEDDED in another app — Discover's CMS Catalog tab iframes the
 * portal's `/cms/catalog`. An iframe is its own document, and portal.localhost is a different origin from
 * discover.localhost, so the host can neither inherit its `<html data-theme>` down nor reach in through
 * `contentDocument` and set it. The query string is the only channel that crosses, and it has to OUTRANK
 * the stored preference: the embed must match the app around it, not whatever this origin's own visitor
 * last chose (they are separate localStorage/cookie jars, so those two routinely disagree).
 */
function forced_theme(): Theme | null {
    if (!has_dom()) return null
    const value = new URLSearchParams(globalThis.location.search).get('theme')
    return value === 'dark' || value === 'light' ? value : null
}

/** The chosen preference (what the switcher shows), from the store. */
export function get_mode(): ThemeMode {
    const mode = $s.theme
    return mode === 'light' || mode === 'dark' ? mode : 'auto'
}

/** Mirror the current `$s.theme` onto `<html data-theme>` (auto clears it → CSS follows the OS). */
export function apply_theme(): void {
    if (!has_dom()) return
    const forced = forced_theme()
    if (forced) {
        document.documentElement.dataset.theme = forced
        return
    }
    const mode = get_mode()
    if (mode === 'auto') delete document.documentElement.dataset.theme
    else document.documentElement.dataset.theme = mode
}

/** Set the preference. Persist it (cookie in portal, localStorage in discover) and sync the DOM. */
export function set_mode(mode: ThemeMode): void {
    $s.theme = mode
    void store.save() // the store does not auto-persist on mutation — mirror set_language()
    apply_theme()
}

/** Cycle light → dark → auto → light (for a single-button 3-state toggle). */
export function cycle_mode(): void {
    const next: Record<ThemeMode, ThemeMode> = {light: 'dark', dark: 'auto', auto: 'light'}
    set_mode(next[get_mode()])
}

/** The theme actually in effect (forced `?theme=`, explicit preference, or the OS choice when on auto). */
export function current_theme(): Theme {
    const forced = forced_theme()
    if (forced) return forced
    const mode = get_mode()
    if (mode !== 'auto') return mode
    return has_dom() && globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Apply the stored preference to the DOM on startup (call once in the browser after the store is loaded). */
export function init_theme(): void {
    apply_theme()
}
